import React, {useState} from "react";
import {useLanguage} from "../configuration/configuration";
import {NavigationGroup, NavigationRow} from "../navigation/layout/nav-layout";
import {RoundedButton, Tooltip} from "../navigation/buttons/nav-buttons";
import {FormRow} from "../forms/manage-spaces/manage-space-form";
import MarkdownTextarea from "./markdown-textarea";
import {cn} from "../util/text-utils";
import {fs_regular, VisibilityIcon, VisibilityOffIcon} from "../navigation/icons/nav-icons";
import {Popover} from "../navigation/popover/popover";
import SpaceAccessIcon from "../navigation/icons/space-access-icon";
import PostVisibilityPopover from "../popover/post-visibility-popover";
import GenericPostVisibilityPopover from "../popover/generic-post-visibility-popover";
import {useIsHomeSpaceLocation} from "../../selectors";
import {getGenericVisibilityFromLocalStorage, getHomeVisibilityFromLocalStorage} from "../../actions/local-storage";

export const EditorMarkdownTextarea = ({id, ...otherProps}) => {
    const [toggleMarkdown, setToggleMarkdown] = useState(false);
    const [visibilityForm, setVisibilityForm] = useState(null);
    const {isHomeSpace} = useIsHomeSpaceLocation();
    const Lang = useLanguage();

    const handleOnToggleMarkdown = event => {
        event.preventDefault();
        setToggleMarkdown(!toggleMarkdown);
    }

    // visibility and access rights for post by home or generic space
    const handleVisibility = ({close}) => {
        const popover = isHomeSpace ?
            <PostVisibilityPopover postId={null} onUpdate={handleOnUpdateVisibility} onClose={close}/> :
            <GenericPostVisibilityPopover postId={null} onUpdate={handleOnUpdateVisibility} onClose={close}/>;

        return <div>{popover}</div>
    }

    // required to update visibility icon
    const handleOnUpdateVisibility = formData => {
        setVisibilityForm(formData);
    };

    const getAccessFromLocalStorage = () => {
        const {access} = isHomeSpace ?
            getHomeVisibilityFromLocalStorage() :
            getGenericVisibilityFromLocalStorage();
        return access;
    }

    return <div className='d-block w-100 mt-2'>
        <NavigationRow>
            <NavigationGroup>
                <Tooltip>
                    <Popover className='popover-status-container'
                             placement='bottom'
                             render={handleVisibility}>
                        <RoundedButton className="btn-outline-secondary icon-padding-adj">
                            <SpaceAccessIcon size={fs_regular} className="role-hover-white-btn"
                                             access={getAccessFromLocalStorage()}/>
                        </RoundedButton>
                    </Popover>
                </Tooltip>
            </NavigationGroup>
            <NavigationGroup>
                <RoundedButton className="btn-outline-secondary p-1"
                               onClick={handleOnToggleMarkdown} title={Lang.markdown.toggle.text}>
                    {!toggleMarkdown ? <VisibilityIcon size={fs_regular}/> : <VisibilityOffIcon big/>}
                </RoundedButton>
            </NavigationGroup>
        </NavigationRow>

        <FormRow>
            <MarkdownTextarea className='d-block w-100' id={id} {...otherProps} onToggle={toggleMarkdown}/>
        </FormRow>
    </div>
}

/**
 * same parameters as for <textarea> including a label. toggles between edit mode and html display mode
 *
 * @param label
 * @param id
 * @param otherProps
 */
const FormMarkdownTextarea = ({label, id, spacing, ...otherProps}) => {
    const [toggleHtml, setToggleHtml] = useState(false);
    const Lang = useLanguage();

    const handleOnToggleHtml = event => {
        event.preventDefault();
        setToggleHtml(!toggleHtml);
    }

    return <div className={cn('mt-2', [spacing])}>
        <NavigationRow>
            <NavigationGroup>
                <label className="form-check-label float-left" htmlFor={id}>
                    {label}
                </label>
            </NavigationGroup>
            <NavigationGroup>
                <RoundedButton className="btn-outline-secondary p-1"
                               onClick={handleOnToggleHtml} title={Lang.markdown.toggle.text}>
                    {!toggleHtml ? <VisibilityIcon size={fs_regular}/> : <VisibilityOffIcon size={fs_regular}/>}
                </RoundedButton>
            </NavigationGroup>
        </NavigationRow>

        <FormRow>
            <MarkdownTextarea id={id} {...otherProps} onToggle={toggleHtml}/>
            <div className="form-text text-muted text-left mb-2">
                <span className='badge bg-secondary rounded-pill mr-1'>MARKDOWN</span>
                {Lang.updateSpace.generalInformation.information}
            </div>
        </FormRow>
    </div>
}

export default FormMarkdownTextarea;
